import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Table, TableBody, TableCell,
  TableHead, TableRow, Chip, Button, Alert, IconButton, TextField, MenuItem,
  Dialog, DialogTitle, DialogContent, DialogActions, Tooltip, Grid,
  InputAdornment, Divider
} from '@mui/material';
import {
  Add, OpenInNew, Delete, Home, Search, Groups, Refresh, WarningAmber,
  PersonAddAlt1, MailOutline, ContentCopy, CheckCircle,
} from '@mui/icons-material';
import axios from 'axios';
import { useUnsavedGuard } from '../hooks/useUnsavedGuard';
import { useDepartments } from '../hooks/useDepartments';
import { isRealDate } from '../utils/dateValidation';

const API = process.env.REACT_APP_API_URL;
const GENDERS     = ['Male', 'Female', 'Other'];
const STATUSES    = ['Active', 'Probation', 'On Leave', 'Terminated'];

const emptyForm = {
  firstName: '', lastName: '', jobTitle: '', department: 'Teaching',
  email: '', phone: '', gender: '', dob: '', nationalId: '',
  employmentDate: '', status: 'Active'
};

const statusColor = { Active: 'success', Probation: 'info', 'On Leave': 'warning', Terminated: 'error' };
const DEPT_COLORS = ['primary', 'secondary', 'warning', 'info', 'success', 'default'];
const deptColor = (name, allNames) => DEPT_COLORS[allNames.indexOf(name) % DEPT_COLORS.length] || 'default';

const EmployeeList = () => {
  const navigate = useNavigate();
  const { names: deptNames } = useDepartments();
  const [employees,  setEmployees]  = useState([]);
  const [filtered,   setFiltered]   = useState([]);
  const [alBalances, setAlBalances] = useState({});
  const [search,     setSearch]     = useState('');
  const [deptFilter, setDeptFilter] = useState('');
  const [error,      setError]      = useState('');
  const [success,    setSuccess]    = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form,       setForm]       = useState(emptyForm);
  const { attemptClose, discardDialog } = useUnsavedGuard({ open: dialogOpen, value: form, onClose: () => setDialogOpen(false) });
  const [saving,     setSaving]     = useState(false);
  const [confirmDlg, setConfirmDlg] = useState(null);
  const [userDlg,      setUserDlg]      = useState(null);  // employee → confirm create login
  const [noEmailDlg,   setNoEmailDlg]   = useState(null);  // employee without an email
  const [userResult,   setUserResult]   = useState(null);  // { name, email, tempPassword }
  const [creatingUser, setCreatingUser] = useState(false);
  const [copied,       setCopied]       = useState(false);
  const [groups,       setGroups]       = useState([]);    // job profiles (user groups)
  const [userGroup,    setUserGroup]    = useState('');

  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
  const year = new Date().getFullYear();

  const load = useCallback(async () => {
    try {
      const [empRes, balRes] = await Promise.all([
        axios.get(`${API}/api/employees`, { headers }),
        axios.get(`${API}/api/leave/balances?leaveTypeCode=AL&year=${year}`, { headers }),
      ]);
      setEmployees(empRes.data);
      setFiltered(empRes.data);
      setAlBalances(balRes.data || {});
    } catch { setError('Failed to load employees'); }
  }, []); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    let result = employees;
    if (deptFilter) result = result.filter(e => e.department === deptFilter);
    if (search) {
      const q = search.toLowerCase();
      result = result.filter(e =>
        e.firstName.toLowerCase().includes(q) ||
        e.lastName.toLowerCase().includes(q)  ||
        e.employeeNo.toLowerCase().includes(q)||
        (e.jobTitle || '').toLowerCase().includes(q)
      );
    }
    setFiltered(result);
  }, [employees, search, deptFilter]);

  const openCreate = () => {
    setForm(emptyForm); setError(''); setDialogOpen(true);
  };

  const handle = (e) => {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));
  };

  const handleSave = async () => {
    if (!form.firstName.trim()) { setError('First name is required'); return; }
    if (!form.lastName.trim())  { setError('Last name is required');  return; }
    setSaving(true); setError('');
    try {
      await axios.post(`${API}/api/employees`, form, { headers });
      setSuccess('Created');
      setDialogOpen(false); load();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Save failed');
    } finally { setSaving(false); }
  };

  const confirmAction = async () => {
    const emp = confirmDlg;
    setConfirmDlg(null);
    try {
      await axios.delete(`${API}/api/employees/${emp.employeeNo}`, { headers });
      setSuccess('Deleted'); load();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) { setError(err.response?.data?.error || 'Delete failed'); }
  };

  // Open the create-user dialog; lazily load the job-profile groups once
  const openUserDlg = async (emp) => {
    setUserDlg(emp);
    setUserGroup(g => g || 'TEACHERS'); // sensible default for a school
    if (groups.length === 0) {
      try {
        const r = await axios.get(`${API}/api/user-groups`, { headers });
        setGroups(r.data || []);
      } catch { /* dropdown just shows the default */ }
    }
  };

  const createUser = async () => {
    const emp = userDlg;
    setCreatingUser(true); setError('');
    try {
      const r = await axios.post(`${API}/api/employees/${emp.employeeNo}/create-user`, { userGroup }, { headers });
      setUserDlg(null);
      setCopied(false);
      setUserResult({ name: `${emp.firstName} ${emp.lastName}`, ...r.data });
    } catch (err) {
      setUserDlg(null);
      if (err.response?.data?.code === 'NO_EMAIL') setNoEmailDlg(emp);
      else setError(err.response?.data?.error || 'Could not create user account');
    } finally { setCreatingUser(false); }
  };

  const activeCount = employees.filter(e => e.status === 'Active').length;

  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto', mt: 4, mb: 4 }}>
      <Card elevation={3}>
        <CardContent>

          {/* Header */}
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2} flexWrap="wrap" gap={1}>
            <Box display="flex" alignItems="center" gap={1}>
              <Groups sx={{ color: '#546e7a' }} />
              <Typography variant="h5" sx={{ color: '#37474f', fontWeight: 700 }}>Employees</Typography>
              <Chip label={`${activeCount} active`} size="small" color="success" sx={{ ml: 0.5 }} />
              <Chip label={employees.length} size="small" variant="outlined" />
            </Box>
            <Box display="flex" gap={1}>
              <Tooltip title="Refresh">
                <IconButton onClick={load} sx={{ bgcolor: 'background.paper', '&:hover': { bgcolor: 'grey.100' } }}>
                  <Refresh />
                </IconButton>
              </Tooltip>
              <Tooltip title="Back to HR">
                <IconButton onClick={() => navigate('/hr')}
                  sx={{ bgcolor: 'background.paper', '&:hover': { bgcolor: 'grey.100' } }}>
                  <Home />
                </IconButton>
              </Tooltip>
              <Button variant="contained" startIcon={<Add />} onClick={openCreate}
                sx={{ bgcolor: '#546e7a', '&:hover': { bgcolor: '#37474f' } }}>
                Add Employee
              </Button>
            </Box>
          </Box>

          {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
          {error   && <Alert severity="error"   sx={{ mb: 2 }}>{error}</Alert>}

          {/* Filters */}
          <Box display="flex" gap={2} mb={2} flexWrap="wrap">
            <TextField
              placeholder="Search name, number, job title…"
              size="small" value={search} onChange={e => setSearch(e.target.value)}
              sx={{ minWidth: 240 }}
              InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
            />
            <TextField select label="Department" size="small" value={deptFilter}
              onChange={e => setDeptFilter(e.target.value)} sx={{ minWidth: 160 }}>
              <MenuItem value=""><em>All Departments</em></MenuItem>
              {deptNames.map(d => <MenuItem key={d} value={d}>{d}</MenuItem>)}
            </TextField>
          </Box>

          <Divider sx={{ mb: 2 }} />

          {/* Table */}
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell><strong>Emp No</strong></TableCell>
                <TableCell><strong>Name</strong></TableCell>
                <TableCell><strong>Job Title</strong></TableCell>
                <TableCell><strong>Department</strong></TableCell>
                <TableCell><strong>Phone / Email</strong></TableCell>
                <TableCell align="center"><strong>Status</strong></TableCell>
                <TableCell align="center"><strong>AL Balance</strong></TableCell>
                <TableCell align="right"><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography variant="body2" color="text.secondary" py={2}>
                      {employees.length === 0 ? 'No employees yet. Click Add Employee to get started.' : 'No results match the filter.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : filtered.map(emp => (
                <TableRow key={emp.employeeNo} hover
                  onClick={() => navigate(`/hr/employees/${emp.employeeNo}`)}
                  sx={{ cursor: 'pointer' }}>
                  <TableCell>
                    <Typography variant="caption" fontFamily="monospace" fontWeight={600}>{emp.employeeNo}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>{emp.firstName} {emp.lastName}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">{emp.jobTitle || <em style={{ color: '#bbb' }}>—</em>}</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip label={emp.department} size="small" color={deptColor(emp.department, deptNames)} variant="outlined" />
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" display="block">{emp.phone || '—'}</Typography>
                    <Typography variant="caption" color="text.secondary">{emp.email || ''}</Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Chip label={emp.status} size="small" color={statusColor[emp.status] || 'default'} />
                  </TableCell>
                  <TableCell align="center" onClick={e => e.stopPropagation()}>
                    {(() => {
                      const bal = alBalances[emp.employeeNo];
                      if (bal === undefined) return <Typography variant="caption" color="text.disabled">—</Typography>;
                      const color = bal <= 0 ? 'error' : bal <= 5 ? 'warning' : 'success';
                      return (
                        <Chip label={`${bal} days`} size="small" color={color} variant="outlined"
                          sx={{ cursor: 'pointer', fontWeight: 700 }}
                          onClick={() => navigate(`/hr/leave/ledger?employeeNo=${emp.employeeNo}&leaveTypeCode=AL`)}
                        />
                      );
                    })()}
                  </TableCell>
                  <TableCell align="right" onClick={e => e.stopPropagation()}>
                    <Tooltip title="Open Employee Card">
                      <IconButton size="small" color="primary"
                        onClick={() => navigate(`/hr/employees/${emp.employeeNo}`)}>
                        <OpenInNew fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Create user account">
                      <IconButton size="small" sx={{ color: '#2e7d32' }}
                        onClick={() => (emp.email ? openUserDlg(emp) : setNoEmailDlg(emp))}>
                        <PersonAddAlt1 fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton size="small" color="error" onClick={() => setConfirmDlg(emp)}><Delete fontSize="small" /></IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {filtered.length > 0 && (
            <Typography variant="caption" color="text.secondary" mt={1} display="block">
              Showing {filtered.length} of {employees.length} employees
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* Create user — confirm */}
      <Dialog open={Boolean(userDlg)} onClose={() => setUserDlg(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PersonAddAlt1 color="success" /> Create User Account
        </DialogTitle>
        <DialogContent>
          <Typography mb={1.5}>
            Create a login account for <strong>{userDlg?.firstName} {userDlg?.lastName}</strong>?
          </Typography>
          <Box display="flex" alignItems="center" gap={1} sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 2, mb: 2 }}>
            <MailOutline sx={{ fontSize: 18, color: 'text.secondary' }} />
            <Typography variant="body2" fontFamily="monospace">{userDlg?.email}</Typography>
          </Box>
          <TextField select fullWidth size="small" label="Job Profile (permissions)"
            value={userGroup} onChange={e => setUserGroup(e.target.value)}
            helperText="What this account may access — change later under Setup → Users">
            <MenuItem value=""><em>No profile yet (assign later)</em></MenuItem>
            {(groups.length ? groups : [{ code: 'TEACHERS', description: 'Class and subject teachers' }]).map(g => (
              <MenuItem key={g.code} value={g.code}>
                {g.code}{g.description ? ` — ${g.description}` : ''}
              </MenuItem>
            ))}
          </TextField>
          <Typography variant="caption" color="text.secondary" display="block" mt={1.5}>
            The email becomes their username. A temporary password is generated for you
            to share — they must change it on first login.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUserDlg(null)}>Cancel</Button>
          <Button variant="contained" color="success" onClick={createUser} disabled={creatingUser}>
            {creatingUser ? 'Creating…' : 'Create Account'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create user — missing email */}
      <Dialog open={Boolean(noEmailDlg)} onClose={() => setNoEmailDlg(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'warning.main' }}>
          <WarningAmber color="warning" /> Email Required
        </DialogTitle>
        <DialogContent>
          <Typography mb={1}>
            <strong>{noEmailDlg?.firstName} {noEmailDlg?.lastName}</strong> has no email
            address on file, so a login account can't be created yet.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            The email becomes their username and is where messages from parents reach
            them. Add one on the employee card, then try again.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNoEmailDlg(null)}>Close</Button>
          <Button variant="contained" startIcon={<OpenInNew />}
            onClick={() => { navigate(`/hr/employees/${noEmailDlg.employeeNo}`); }}>
            Open Employee Card
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create user — success with temp password */}
      <Dialog open={Boolean(userResult)} onClose={() => setUserResult(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'success.main' }}>
          <CheckCircle color="success" /> Account Created
        </DialogTitle>
        <DialogContent>
          <Typography mb={2}>
            <strong>{userResult?.name}</strong> can now sign in with:
          </Typography>
          <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 2, mb: 1.5 }}>
            <Typography variant="caption" color="text.secondary">Username / Email</Typography>
            <Typography variant="body2" fontFamily="monospace" fontWeight={700} mb={1.5}>
              {userResult?.email}
            </Typography>
            <Typography variant="caption" color="text.secondary">Temporary Password</Typography>
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="h6" fontFamily="monospace" fontWeight={800} letterSpacing={2}>
                {userResult?.tempPassword}
              </Typography>
              <Tooltip title={copied ? 'Copied!' : 'Copy password'}>
                <IconButton size="small" onClick={() => {
                  navigator.clipboard?.writeText(userResult?.tempPassword || '');
                  setCopied(true);
                }}>
                  {copied ? <CheckCircle fontSize="small" color="success" /> : <ContentCopy fontSize="small" />}
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
          <Alert severity="info" sx={{ borderRadius: 2 }}>
            Share this securely — it is shown only once, and {userResult?.name?.split(' ')[0]} must
            change it on first login.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => setUserResult(null)}>Done</Button>
        </DialogActions>
      </Dialog>

      {/* Confirm Delete Dialog */}
      <Dialog open={Boolean(confirmDlg)} onClose={() => setConfirmDlg(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'error.main' }}>
          <WarningAmber color="error" /> Delete Employee
        </DialogTitle>
        <DialogContent>
          <Typography>
            Permanently delete <strong>{confirmDlg?.firstName} {confirmDlg?.lastName}</strong> ({confirmDlg?.employeeNo})? All their records will be removed.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDlg(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={confirmAction}>Delete</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog */}
      {discardDialog}
      <Dialog open={dialogOpen} onClose={attemptClose} maxWidth="md" fullWidth>
        <DialogTitle>Add Employee</DialogTitle>
        <DialogContent dividers>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <Grid container spacing={2}>

            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase">
                Personal Details
              </Typography>
              <Divider sx={{ mt: 0.5, mb: 1.5 }} />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField label="First Name *" name="firstName" value={form.firstName}
                onChange={handle} fullWidth size="small" />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField label="Last Name *" name="lastName" value={form.lastName}
                onChange={handle} fullWidth size="small" />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField select label="Gender" name="gender" value={form.gender}
                onChange={handle} fullWidth size="small">
                <MenuItem value=""><em>— Not set —</em></MenuItem>
                {GENDERS.map(g => <MenuItem key={g} value={g}>{g}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField label="Date of Birth" name="dob" type="date" value={form.dob}
                onChange={handle} fullWidth size="small" InputLabelProps={{ shrink: true }}
                inputProps={{ max: new Date().toISOString().slice(0, 10) }}
                error={!!(form.dob && !isRealDate(form.dob))}
                helperText={form.dob && !isRealDate(form.dob) ? 'Not a valid date' : ''} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField label="National ID" name="nationalId" value={form.nationalId}
                onChange={handle} fullWidth size="small" />
            </Grid>

            <Grid item xs={12} sx={{ mt: 1 }}>
              <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase">
                Employment
              </Typography>
              <Divider sx={{ mt: 0.5, mb: 1.5 }} />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField label="Job Title" name="jobTitle" value={form.jobTitle}
                onChange={handle} fullWidth size="small" placeholder="e.g. Mathematics Teacher"
                InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField select label="Department" name="department" value={form.department}
                onChange={handle} fullWidth size="small" InputLabelProps={{ shrink: true }}
                SelectProps={{ displayEmpty: true, MenuProps: { PaperProps: { sx: { maxHeight: 280 } } } }}>
                {deptNames.map(d => <MenuItem key={d} value={d}>{d}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField select label="Status" name="status" value={form.status}
                onChange={handle} fullWidth size="small" InputLabelProps={{ shrink: true }}
                SelectProps={{ displayEmpty: true, MenuProps: { PaperProps: { sx: { maxHeight: 280 } } } }}>
                {STATUSES.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField label="Employment Date" name="employmentDate" type="date" value={form.employmentDate}
                onChange={handle} fullWidth size="small" InputLabelProps={{ shrink: true }}
                inputProps={{ min: form.dob || undefined }}
                error={!!((form.employmentDate && !isRealDate(form.employmentDate)) || (form.dob && form.employmentDate && form.employmentDate < form.dob))}
                helperText={(form.employmentDate && !isRealDate(form.employmentDate)) ? 'Not a valid date' : (form.dob && form.employmentDate && form.employmentDate < form.dob) ? 'Before date of birth' : ''} />
            </Grid>

            <Grid item xs={12} sx={{ mt: 1 }}>
              <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase">
                Contact
              </Typography>
              <Divider sx={{ mt: 0.5, mb: 1.5 }} />
            </Grid>

            <Grid item xs={12} sm={5}>
              <TextField label="Email" name="email" type="email" value={form.email}
                onChange={handle} fullWidth size="small" />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField label="Phone" name="phone" value={form.phone}
                onChange={handle} fullWidth size="small" />
            </Grid>

          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={attemptClose}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}
            sx={{ bgcolor: '#546e7a', '&:hover': { bgcolor: '#37474f' } }}>
            {saving ? 'Saving…' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EmployeeList;
